/**
 * CacheStorage - Handles localStorage persistence for organization cache
 * Extracted from OrganizationCache to follow Single Responsibility Principle
 */

import logger from '../../../shared/services/logging/logger';
import { asyncStorage } from '../../../shared/services/storage/asyncStorageService';
import { CACHE_STORAGE_KEYS } from '../config/cacheConfig';

export class CacheStorage {
  /**
   * Save cache data and expiry times to localStorage
   * @param {Object} cache - The cache data object
   * @param {Object} expiryTimes - The expiry times object
   */
  saveToStorage(cache, expiryTimes) {
    try {
      asyncStorage.set(CACHE_STORAGE_KEYS.CACHE_DATA, JSON.stringify(cache));
      asyncStorage.set(CACHE_STORAGE_KEYS.CACHE_EXPIRY, JSON.stringify(expiryTimes));
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        logger.warn('localStorage quota exceeded, clearing cache', { error: e.message });
        throw e; // Let caller handle quota exceeded
      } else {
        logger.error('Failed to save cache to localStorage', { error: e.message });
      }
    }
  }

  /**
   * Load cache data and expiry times from localStorage
   * @returns {Object} Object containing cache and expiryTimes, or null if not found
   */
  loadFromStorage() {
    try {
      const cachedData = asyncStorage.get(CACHE_STORAGE_KEYS.CACHE_DATA);
      const cachedExpiry = asyncStorage.get(CACHE_STORAGE_KEYS.CACHE_EXPIRY);

      if (cachedData && cachedExpiry) {
        const loadedCache = JSON.parse(cachedData);
        const loadedExpiry = JSON.parse(cachedExpiry);

        // Ensure all required cache types exist
        const cache = {
          roles: loadedCache.roles || {},
          members: loadedCache.members || {},
          divisions: loadedCache.divisions || {},
          groups: loadedCache.groups || {},
          invitations: loadedCache.invitations || {},
          organization: loadedCache.organization || {},
          notifications: loadedCache.notifications || {}
        };

        const expiryTimes = {
          roles: loadedExpiry.roles || {},
          members: loadedExpiry.members || {},
          divisions: loadedExpiry.divisions || {},
          groups: loadedExpiry.groups || {},
          invitations: loadedExpiry.invitations || {},
          organization: loadedExpiry.organization || {},
          notifications: loadedExpiry.notifications || {}
        };

        return { cache, expiryTimes };
      }

      return null;
    } catch (e) {
      logger.error('Failed to load cache from localStorage', { error: e.message });
      return null;
    }
  }

  /**
   * Clear all cache data from localStorage
   */
  clearStorage() {
    try {
      asyncStorage.remove(CACHE_STORAGE_KEYS.CACHE_DATA);
      asyncStorage.remove(CACHE_STORAGE_KEYS.CACHE_EXPIRY);
    } catch (e) {
      logger.error('Failed to clear cache from localStorage', { error: e.message });
    }
  }

  /**
   * Clean expired items from cache
   * @param {Object} cache - The cache data object
   * @param {Object} expiryTimes - The expiry times object
   * @returns {number} Number of expired items removed
   */
  cleanExpiredItems(cache, expiryTimes) {
    let expiredCount = 0;
    const now = Date.now();

    Object.keys(expiryTimes).forEach(dataType => {
      if (expiryTimes[dataType]) {
        Object.keys(expiryTimes[dataType]).forEach(key => {
          if (expiryTimes[dataType][key] < now) {
            if (cache[dataType]) {
              delete cache[dataType][key];
            }
            delete expiryTimes[dataType][key];
            expiredCount++;
          }
        });
      }
    });

    return expiredCount;
  }

  /**
   * Initialize empty cache structure
   * @returns {Object} Object containing empty cache and expiryTimes structures
   */
  initializeEmptyCache() {
    const cache = {
      roles: {},
      members: {},
      divisions: {},
      groups: {},
      invitations: {},
      organization: {},
      notifications: {}
    };

    const expiryTimes = {
      roles: {},
      members: {},
      divisions: {},
      groups: {},
      invitations: {},
      organization: {},
      notifications: {}
    };

    return { cache, expiryTimes };
  }
}
